import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import ImagePreview from '@/components/forms/imagePreview';
import Input from '@/components/forms/Input';
import { Upload } from '@/components/upload';
import { lineUrlRegex } from '@/constants/regex';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { FileObject } from '@/lib/uploadToAWS';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import z from 'zod';
import { useLineGroupMutation } from '../hooks/useLineGroupMutation';
import { buildLineGroupPayload } from '../utils/buildLineGroupPayload';

const formSchema = z
  .object({
    lineGroupName: z.string().min(1, { message: 'common.form.validation.required' }),
    existingImageSrc: z.string().optional().default(''),
    image: optionalUploadValueSchema,
    lineGroupLink: z.string().regex(lineUrlRegex, 'common.form.validation.enterValidUrl').or(z.literal('')),
  })

  .superRefine((val, ctx) => {
    const hasExisting = !!val.existingImageSrc;
    const hasNewFile = !!val.image && typeof val.image === 'object';
    if (!hasExisting && !hasNewFile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['image'],
        message: 'common.form.validation.required',
      });
    }
  });
export type FormValues = z.infer<typeof formSchema>;
const ManageStoreLineGroupFormView = ({
  clubid,
  defaultValues,
}: {
  clubid: string;
  defaultValues: {
    lineGroupName: string;
    existingImageSrc: string;
    image: string;
    lineGroupLink: string;
  };
}) => {
  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: {
      lineGroupName: defaultValues.lineGroupName,
      existingImageSrc: defaultValues.existingImageSrc,
      image: '',
      lineGroupLink: defaultValues.lineGroupLink,
    },
  });
  const { t } = useDynamicLocale();
  const { mutateAsync } = useLineGroupMutation();
  const existingImageSrc = form.watch('existingImageSrc');
  const [imagePreview, setImagePreview] = useState(defaultValues.image || '');
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  useEffect(() => {
    return () => {
      if (cropSource?.startsWith('blob:')) {
        URL.revokeObjectURL(cropSource);
      }
    };
  }, [cropSource]);

  return (
    <>
      <Form
        form={form}
        onSubmit={async (data) => {
          const payload = await buildLineGroupPayload(data);
          await mutateAsync({ clubId: clubid, ...payload });
        }}
      >
        <div className="w-full space-y-4.5">
          <div className=" gap-2.5 flex flex-col">
            <div>
              <div className="text-control font-bold color-primary flex items-center mb-2"></div>
              <Input
                label="common.form.label.lineGroupName"
                forceRequired
                {...form.register('lineGroupName')}
                placeholder={t('common.form.label.lineGroupName')}
                maxLength={50}
              />
            </div>
            <div>
              <div className="text-control font-bold color-primary flex items-center mb-2"></div>
              {existingImageSrc ? (
                <ImagePreview
                  isRequired
                  label="common.form.label.qrCode"
                  variant="rect"
                  imageClassName="w-full h-[120px] !object-contain"
                  imageSrc={existingImageSrc}
                  alt={''}
                  cancelClickHandler={() => {
                    form.setValue('existingImageSrc', '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
              ) : (
                <Controller
                  name="image"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Upload
                      key={uploadKey}
                      label="common.form.label.qrCode"
                      isRequired
                      maxSizeMB={5}
                      variant="rect"
                      accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                      multiple={false}
                      buttonText={t('configurationManagement.location.page.editModal.browseFile')}
                      error={fieldState.error?.message}
                      defaultPreviewUrl={imagePreview}
                      onFilesChange={(files) => {
                        const selected = files?.[0];

                        if (!selected || typeof selected === 'string') {
                          field.onChange('');
                          return;
                        }

                        const rawFile = selected.file as File;
                        const localPreview = selected.preview || URL.createObjectURL(rawFile);
                        setCropSource(localPreview);
                        setIsCropOpen(true);
                      }}
                      onDefaultPreviewRemove={() => {
                        setImagePreview('');
                        form.setValue('image', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      previewClassName="!object-contain rounded-sm h-[120px] w-full p-0"
                      dropZoneClassName="rounded-sm bg-surface-subtle"
                    />
                  )}
                />
              )}
              <div className="text-caption font-medium text-muted-foreground">
                <Translation id="common.form.placeholder.imageSizeWarning" />
              </div>
            </div>
            <div>
              <Input
                label="common.form.label.lineGroupLink"
                {...form.register('lineGroupLink')}
                placeholder={t('common.form.placeholder.lineGroupLink')}
                maxLength={50}
              />
            </div>
          </div>
        </div>
      </Form>
      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        type="store"
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setCropSource(null);
          setUploadKey((k) => k + 1);
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: croppedFile,
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setImagePreview(previewUrl);

          form.setValue('image', croppedFileObject, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </>
  );
};

export default ManageStoreLineGroupFormView;
